import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../utils/api";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { trackSelfSignupPurchase } from "../../utils/trackSelfSignupPurchase";

// The Stripe webhook completes subscription signups (creates the account and
// sends the activation email). This page polls for that completion instead of
// triggering processing itself, which previously raced the webhook. Only if the
// webhook hasn't completed within the timeout (or for one-off payment signups,
// which have no webhook) does it fall back to the payment-success endpoint.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 45000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [isCoffeePass, setIsCoffeePass] = useState(false);
  const [activationInviteCode, setActivationInviteCode] = useState(null);
  const sessionId = searchParams.get("session_id");
  const isAdminMembership = searchParams.get("admin_membership") === "true";
  // A self-signup member activates next, so send them to the invite-code step rather than the
  // homepage, where a login already stored in this browser would open someone else's portal.
  const toActivation = Boolean(sessionId) && !isAdminMembership && success;
  const continueLabel = !sessionId ? "Go to Login" : toActivation ? "Enter your invite code" : "Go to Homepage";
  const onContinue = () => {
    if (!sessionId) window.location.href = "/login";
    else if (toActivation) {
      const params = new URLSearchParams({ selfsignup: "true" });
      if (activationInviteCode) {
        params.set("code", activationInviteCode);
      }
      window.location.href = `/activate?${params.toString()}`;
    } else window.location.href = "/";
  };

  const hasStarted = useRef(false); // Prevent duplicate flows within one mount
  const hasTrackedPurchase = useRef(false);

  const firePurchaseAnalytics = (analytics) => {
    if (hasTrackedPurchase.current) return;
    if (!analytics?.transaction_id) return;
    hasTrackedPurchase.current = true;
    trackSelfSignupPurchase(analytics, { sessionId })
      .then((tracked) => {
        if (!tracked) hasTrackedPurchase.current = false;
      })
      .catch(() => {
        hasTrackedPurchase.current = false;
      });
  };

  useEffect(() => {
    if (!sessionId) {
      showErrorToast("Invalid link: session ID is missing.");
      setLoading(false);
      // Redirect to login page after a short delay
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
      return;
    }

    if (hasStarted.current) return;
    hasStarted.current = true;

    // Fallback: trigger server-side processing directly (also handles legacy
    // signups and missed webhooks)
    const confirmPaymentDirectly = async () => {
      try {
        const response = await api("v1/selfsignup/payment-success", "POST", { sessionId });
        // Prefer success: webhook may finish first (alreadyProcessed). Only treat
        // duplicatePayment as failure when the API did not confirm success (e.g. 409).
        if (response.data?.success) {
          if (response.data.inviteCode) {
            setActivationInviteCode(String(response.data.inviteCode).toUpperCase());
          }
          if (response.data.alreadyProcessed) {
            showSuccessToast(
              isAdminMembership
                ? "Payment already confirmed. Your membership is active."
                : "Your payment has already been confirmed."
            );
          } else {
            showSuccessToast(
              isAdminMembership
                ? "Payment confirmed. Your membership is now active."
                : "Your payment has been confirmed!"
            );
          }
          firePurchaseAnalytics(response.data.analytics);
          setSuccess(true);
        } else if (response.data?.duplicatePayment) {
          showErrorToast(
            response.data.message ||
              "A payment was already processed for this signup. If you were charged more than once, please contact support."
          );
          setSuccess(false);
        } else {
          showErrorToast(response.data.message || "Payment confirmation failed.");
        }
      } catch (error) {
        console.error("Error confirming payment:", error);
        showErrorToast("An unexpected error occurred while verifying your payment.");
      } finally {
        setLoading(false);
      }
    };

    const checkStatus = async (includeAnalytics = false) => {
      const query = includeAnalytics ? "?includeAnalytics=true" : "";
      const response = await api(
        `v1/selfsignup/payment-status/${sessionId}${query}`,
        "GET"
      );
      if (!response.data?.success) return null;
      return response.data.data; // { status, complete, mode, analytics, inviteCode }
    };

    const waitForWebhookCompletion = async () => {
      let initialStatus = null;
      try {
        initialStatus = await checkStatus();
      } catch (error) {
        console.error("Error checking payment status:", error);
      }
      setIsCoffeePass(initialStatus?.vertical === "cafe");

      // Unknown signup (legacy) or one-off payment (no webhook writer):
      // process directly, same as the old behavior
      if (!initialStatus || initialStatus.mode === "payment") {
        await confirmPaymentDirectly();
        return;
      }

      if (initialStatus.complete) {
        const withAnalytics = (await checkStatus(true)) || initialStatus;
        showSuccessToast(
          isAdminMembership
            ? "Payment confirmed. Your membership is now active."
            : "Your payment has been confirmed!"
        );
        firePurchaseAnalytics(withAnalytics?.analytics);
        const invite = withAnalytics?.inviteCode || initialStatus.inviteCode;
        if (invite) {
          setActivationInviteCode(String(invite).toUpperCase());
        }
        setSuccess(true);
        setLoading(false);
        return;
      }

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        try {
          const status = await checkStatus();
          if (status?.complete) {
            const withAnalytics = (await checkStatus(true)) || status;
            showSuccessToast(
              isAdminMembership
                ? "Payment confirmed. Your membership is now active."
                : "Your payment has been confirmed!"
            );
            firePurchaseAnalytics(withAnalytics.analytics);
            const invite = withAnalytics.inviteCode || status.inviteCode;
            if (invite) {
              setActivationInviteCode(String(invite).toUpperCase());
            }
            setSuccess(true);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error polling payment status:", error);
          // Keep polling until the deadline; transient errors are fine
        }
      }

      // Webhook didn't complete in time - fall back to direct processing
      console.warn("Payment status polling timed out, falling back to direct confirmation");
      await confirmPaymentDirectly();
    };

    waitForWebhookCompletion();
  }, [sessionId, isAdminMembership]);

  const renderMessage = () => {
    if (loading) {
      return <p className="text-gray-600">We’re processing your payment. Hang tight, this won’t take long.</p>;
    }
    if (success) {
      if (isAdminMembership) {
        return (
          <div className="text-gray-700 mb-6 space-y-3 text-left">
            <p>Your payment was successful and your membership is now linked. 🎉</p>
            <p>
              If you already have login details, you can sign in to your Priority One account. Otherwise check your
              email for activation instructions.
            </p>
            <p>
              Need help? Contact{" "}
              <a href="mailto:info@priorityonewashclub.com" className="text-blue-600 underline">
                info@priorityonewashclub.com
              </a>
              .
            </p>
          </div>
        );
      }
      return (
        <div className="text-gray-700 mb-6 space-y-3 text-left">
          <p>
            Your payment was successful, and your {isCoffeePass ? "Coffee Pass" : "registration"} is complete! 🎉
          </p>
          <p>
            Within the next few minutes, an email will arrive in your inbox with your invite code and a secure
            activation link. Click the link, or enter the code using the button below, to:
          </p>
          {isCoffeePass ? (
            <ul className="list-disc list-inside text-gray-700">
              <li>Set up your account password</li>
              <li>Open your Coffee Pass</li>
              <li>Show the live QR code to cafe staff when you order</li>
            </ul>
          ) : (
            <ul className="list-disc list-inside text-gray-700">
              <li>Set up your account password</li>
              <li>Enter your vehicle details</li>
              <li>Access your personalized dashboard</li>
            </ul>
          )}
          <p>
            Please allow up to 10 minutes for the email to appear, and remember to check your Spam or Promotions
            folders.
          </p>
          <p>
            If you don’t receive the email within 10 minutes or encounter any issues, our support team is ready to help.
            Contact us at{" "}
            <a href="mailto:info@priorityonewashclub.com" className="text-blue-600 underline">
              info@priorityonewashclub.com
            </a>
            .
          </p>
        </div>
      );
    }
    return (
      <p className="text-gray-600">
        We couldn’t confirm your payment. If you believe this is an error, please reach out to our support team for
        assistance.
      </p>
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <div className="bg-white p-8 shadow-md rounded-lg text-center max-w-lg">
        <h2 className="text-2xl font-bold mb-4">
          {loading ? "Processing Your Payment..." : success ? "Payment Successful!" : "Payment Confirmation Failed"}
        </h2>
        {renderMessage()}
        {!loading && (
          <button
            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold"
            onClick={onContinue}
          >
            {continueLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
